import { useEffect, useRef, useState } from "react";
import api from "../axiosConfig";

function LoginForm({ onLoginSuccess, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const errorRef = useRef(null);
  const isRegisterMode = mode === "register";

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (isRegisterMode) {
      if (!form.name.trim()) {
        setError("Name is required");
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
    }

    setSubmitting(true);

    try {
      const payload = isRegisterMode
        ? {
            name: form.name.trim(),
            email: form.email,
            password: form.password,
          }
        : {
            email: form.email,
            password: form.password,
          };
      const res = await api.post(isRegisterMode ? "/api/register" : "/api/login", payload);
      if (isRegisterMode && res.data?.pendingApproval) {
        setMessage(res.data.message || "Registration submitted. Wait for admin approval before signing in.");
        setMode("login");
        setForm({ name: "", email: form.email, password: "", confirmPassword: "" });
        return;
      }
      onLoginSuccess?.(res.data.user || null);
    } catch (err) {
      setError(err.response?.data?.error || (isRegisterMode ? "Registration failed" : "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    setSubmitting(false);
    setShowPassword(false);
    setForm((current) => ({
      ...current,
      name: nextMode === "register" ? current.name : "",
      password: "",
      confirmPassword: "",
    }));
  };

  return (
    <section className="login-card">
      <div className="login-card-header">
        <span className="eyebrow">{isRegisterMode ? "Create account" : "Sign in"}</span>
        <h2>{isRegisterMode ? "Create your workspace" : "Open your notes"}</h2>
        <p>
          {isRegisterMode
            ? "Register a new account from this page, then start creating and organizing note titles."
            : "Use your account to access the AddaNote dashboard and continue where you left off."}
        </p>
      </div>

      <div className="auth-mode-switch" role="tablist" aria-label="Authentication mode">
        <button
          type="button"
          className={`auth-mode-button ${!isRegisterMode ? "is-active" : ""}`}
          aria-pressed={!isRegisterMode}
          onClick={() => switchMode("login")}
        >
          Sign in
        </button>
        <button
          type="button"
          className={`auth-mode-button ${isRegisterMode ? "is-active" : ""}`}
          aria-pressed={isRegisterMode}
          onClick={() => switchMode("register")}
        >
          Create account
        </button>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        {isRegisterMode ? (
          <label>
            <span>Name</span>
            <input
              name="name"
              type="text"
              placeholder="Your display name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </label>
        ) : null}

        <label>
          <span>Email</span>
          <input
            name="email"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          <span>Password</span>
          <div className="password-row">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={form.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="action-link"
              onClick={() => setShowPassword((current) => !current)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        {isRegisterMode ? (
          <label>
            <span>Confirm password</span>
            <input
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Repeat password"
              value={form.confirmPassword}
              onChange={handleChange}
              required
            />
          </label>
        ) : null}

        {error ? <div ref={errorRef} className="feedback-panel error" tabIndex={-1}>{error}</div> : null}
        {message ? <div className="feedback-panel">{message}</div> : null}

        {isRegisterMode ? (
          <p className="auth-helper-text">Password must be at least 8 characters. New accounts require admin authorization before first sign-in.</p>
        ) : null}

        <button className="action-button w-100" type="submit" disabled={submitting}>
          {submitting
            ? (isRegisterMode ? "Creating account..." : "Signing in...")
            : (isRegisterMode ? "Create account" : "Login")}
        </button>
      </form>
    </section>
  );
}

export default LoginForm;

